import { memo, useCallback, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  stepConnectorClasses,
  useTheme,
} from '@mui/material';
import { Check, Save } from 'lucide-react';
import type { DeepPartial } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import { CreateLeadAddressStep } from '@modules/_shared/components/create-lead/CreateLeadAddressStep';
import {
  type ContactFormData,
  CreateLeadContactStep,
} from '@modules/_shared/components/create-lead/CreateLeadContactStep';
import { CreateLeadPropertyDetailsStep } from '@modules/_shared/components/create-lead/CreateLeadPropertyDetailsStep';
import type { PropertyDetailsStepForm } from '@modules/_shared/components/create-listing/PropertyDetailsStep';
import type { RaAddressType } from '@modules/_shared/components/form/RaAddressInput';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import {
  type CreateLeadInput,
  useCreateLead,
} from '@modules/_shared/hooks/useCreateLead';
import { useScrollActiveStepIntoView } from '@modules/_shared/hooks/useScrollActiveStepIntoView';
import { useLocale } from '@shared/hooks/locale';

type StepId = 'address' | 'contact' | 'propertyDetails';

type FormData = {
  address: RaAddressType;
  contact: ContactFormData;
  propertyDetails: PropertyDetailsStepForm;
};

type StepComponentProps<T> = {
  data: T;
  footerActionsComponent: React.ReactNode;
  onFormSubmitted: (data: Partial<T>) => Promise<void>;
};

type StepConfig<T> = {
  id: StepId;
  label: string;
  component: React.FC<StepComponentProps<T>>;
};

const initialFormData: DeepPartial<FormData> = {
  address: {},
  contact: {},
  propertyDetails: {},
};

type LeadStepperNavProps = {
  currentStep: number;
  steps: Array<StepConfig<any>>;
};

export const GET_CONTACT = gql(/* GraphQL */ `
  query GetCreateLeadContact($id: uuid!) {
    users_by_pk(id: $id) {
      id
      ...UserSelect
    }
  }
`);

const CustomConnector = () => (
  <StepConnector
    sx={{
      [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 16,
      },
      [`& .${stepConnectorClasses.line}`]: {
        height: 2,
        border: 0,
        backgroundColor: 'grey.200',
        borderRadius: 1,
      },
      [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}, &.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]:
        {
          backgroundColor: 'primary.main',
        },
    }}
  />
);

const StepIcon = ({
  active,
  completed,
  stepNumber,
}: {
  active: boolean;
  completed: boolean;
  stepNumber: number;
}) => {
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: completed || active ? 'primary.main' : 'grey.100',
        color: completed || active ? 'common.white' : 'text.secondary',
        fontSize: '0.875rem',
        fontWeight: 600,
        transition: 'all 0.2s ease',
      }}
    >
      {completed ? (
        <Check size={16} color={palette.common.white} />
      ) : (
        stepNumber
      )}
    </Box>
  );
};

// Stable factory so React doesn't unmount/remount StepIcon on every render.
const makeStepIcon = (stepNumber: number) =>
  function StepIconComponent({
    active,
    completed,
  }: {
    active?: boolean;
    completed?: boolean;
  }) {
    return (
      <StepIcon
        active={!!active}
        completed={!!completed}
        stepNumber={stepNumber}
      />
    );
  };

const LeadStepperNav = memo<LeadStepperNavProps>(({ currentStep, steps }) => {
  const stepIconComponents = useMemo(
    () => steps.map((_, index) => makeStepIcon(index + 1)),
    [steps],
  );
  const scrollRef = useScrollActiveStepIntoView<HTMLDivElement>(currentStep);

  return (
    <Box
      ref={scrollRef}
      sx={{
        borderBottom: '1px solid',
        borderColor: 'grey.200',
        bgcolor: 'background.paper',
        overflowX: 'auto',
      }}
    >
      <Stepper
        activeStep={currentStep}
        alternativeLabel
        connector={<CustomConnector />}
        sx={{ py: 2, px: 3 }}
      >
        {steps.map(({ label }, index) => (
          <Step key={label} sx={{ minWidth: '100px' }}>
            <StepLabel
              sx={{
                '& .MuiStepLabel-label': {
                  mt: 1,
                  fontSize: '0.8125rem',
                  fontWeight: currentStep === index ? 600 : 400,
                  color:
                    currentStep === index ? 'text.primary' : 'text.secondary',
                },
              }}
              slots={{
                stepIcon: stepIconComponents[index],
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
});

LeadStepperNav.displayName = 'LeadStepperNav';

type CreateLeadStepperProps = {
  onLeadCreated: (leadId: string) => void;
  userId?: string;
  // Prefills the address step (e.g. create-lead from a map parcel).
  initialAddress?: RaAddressType;
};

export const CreateLeadStepper = ({
  onLeadCreated,
  userId = '',
  initialAddress,
}: CreateLeadStepperProps) => {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const stageId = searchParams.get('stage_id');

  const [leadErrorLike, leadLoading, createNewLead, resetError] =
    useCreateLead();

  const [getContact] = useLazyQuery(GET_CONTACT);

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<DeepPartial<FormData>>({
    ...initialFormData,
    address: { ...initialFormData.address, ...initialAddress },
    contact: {
      ...initialFormData.contact,
      contact_id: userId,
    },
  });

  const steps = useMemo<
    [
      StepConfig<RaAddressType>,
      StepConfig<ContactFormData>,
      StepConfig<PropertyDetailsStepForm>,
    ]
  >(
    () => [
      {
        id: 'address',
        label: t('Address'),
        component: CreateLeadAddressStep,
      },
      {
        id: 'contact',
        label: t('Contact'),
        component: CreateLeadContactStep,
      },
      {
        id: 'propertyDetails',
        label: t('Property details'),
        component: CreateLeadPropertyDetailsStep,
      },
    ],
    [t],
  );

  const handleCreateLead = useCallback(
    async (finalFormData: FormData) => {
      const contact = await getContact({
        variables: { id: finalFormData.contact.contact_id },
      }).catch(() => ({ data: undefined }));
      // The address carries a Google place id for the autocomplete widget;
      // `properties` has no such column, so Hasura rejects the whole insert
      // if it reaches the input (a prefilled address always carries one).
      const { google_place_id: _googlePlaceId, ...address } =
        finalFormData.address;
      const leadInput: CreateLeadInput = {
        stage_id: stageId,
        contact: {
          id: finalFormData.contact.contact_id,
          first_name: contact.data?.users_by_pk?.first_name ?? '',
          last_name: contact.data?.users_by_pk?.last_name ?? '',
        },
        relationship: finalFormData.contact.relationship,
        sale_horizon: finalFormData.contact.sale_horizon,
        source: finalFormData.contact.source,
        property: {
          ...address,
          ...finalFormData.propertyDetails,
        },
      };

      const createLeadResult = await createNewLead(leadInput);

      if (createLeadResult == null) {
        return null;
      }

      onLeadCreated(createLeadResult.leadId);
      setFormData(initialFormData);
      setActiveStep(0);
    },
    [createNewLead, onLeadCreated, stageId, getContact],
  );

  const onFormSubmitted = useCallback(
    async (data: Partial<FormData[StepId]>) => {
      try {
        if (activeStep === steps.length - 1) {
          const stepId = steps[activeStep].id;
          const finalFormData = {
            ...formData,
            [stepId]: { ...formData[stepId], ...data },
          };
          await handleCreateLead(finalFormData as FormData);
        } else {
          const stepId = steps[activeStep].id;
          setFormData(prev => ({
            ...prev,
            [stepId]: { ...prev[stepId], ...data },
          }));
          setActiveStep(prevStep => prevStep + 1);
        }
      } catch (error) {
        console.error('Form submission failed:', error);
      }
    },
    [activeStep, steps, formData, handleCreateLead],
  );

  const handleBack = useCallback(() => {
    setActiveStep(prevStep => Math.max(0, prevStep - 1));
  }, []);

  const CurrentStepComponent = steps[activeStep].component as React.FC<
    StepComponentProps<FormData[StepId]>
  >;
  const currentStepData = formData[steps[activeStep].id] ?? {};

  return (
    <>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', flex: 1, height: 0 }}
      >
        <LeadStepperNav currentStep={activeStep} steps={steps} />

        <Box
          sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex' }}
        >
          <CurrentStepComponent
            data={currentStepData as FormData[StepId]}
            onFormSubmitted={onFormSubmitted}
            footerActionsComponent={
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderTop: '1px solid',
                  borderColor: 'grey.200',
                  bgcolor: 'background.paper',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Button
                  variant="outlined"
                  color="inherit"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  sx={{ color: 'text.secondary', borderColor: 'grey.300' }}
                >
                  {t('Back')}
                </Button>
                <LoadingButton
                  color="primary"
                  variant="contained"
                  type="submit"
                  loading={leadLoading}
                  loadingPosition={
                    activeStep === steps.length - 1 ? 'start' : undefined
                  }
                  startIcon={
                    activeStep === steps.length - 1 ? <Save /> : undefined
                  }
                >
                  {activeStep === steps.length - 1 ? t('Finish') : t('Next')}
                </LoadingButton>
              </Box>
            }
          />
        </Box>
      </Box>

      <MutationErrorSnackbar error={leadErrorLike} onClose={resetError} />
    </>
  );
};

CreateLeadStepper.displayName = 'CreateLeadStepper';
